<template>
  <div class="groups-step">
    <Typography variant="h-5">Who should have access to this survey?</Typography>

    <v-radio-group
      :value="visibilityType"
      @change="handleVisibilityChange"
      :disabled="isEditing"
      class="mt-0"
      hide-details
    >
      <v-radio label="Public" value="PUBLIC" color="brandPrimary" />
      <v-radio label="Internal" value="PRIVATE" color="brandPrimary" />
    </v-radio-group>
    <Typography variant="body-2"
      >Public/Internal can't be changed after creation, but you can still manage group
      access.</Typography
    >

    <template v-if="visibilityType === 'PRIVATE'">
      <div class="d-flex align-center">
        <v-text-field
          v-model="debounceSearch"
          label="Search"
          solo
          flat
          dense
          hide-details
          outlined
          prepend-inner-icon="mdi-magnify"
          class="search-field mr-4"
        />
        <Button
          v-if="permissions.includes('groups:create:group')"
          outlined
          iconLeft="mdi-plus"
          @click="showGroupCreator = true"
        >
          Group
        </Button>
      </div>

      <div class="d-flex align-center flex-wrap gap-4" v-if="selectedGroups.length">
        <InputChip
          v-for="(group, index) in selectedGroups"
          :key="group.groupId"
          class="ma-1"
          :text="group.groupDisplayName"
          closeable
          @close="removeGroup(index)"
        />
      </div>

      <Typography variant="body-1">Directory</Typography>

      <GroupTable
        ref="groupTable"
        class="full-width"
        :selected="selectedGroups"
        :search="search"
        :includeEveryone="true"
        :displayHeaders="false"
        @select-item="toggleGroup"
      />
    </template>

    <v-navigation-drawer v-model="showGroupCreator" temporary fixed right width="500">
      <GroupTypeSelector
        v-if="showGroupCreator"
        source="survey-builder"
        :version="3"
        :clientId="clientId"
        @close="showGroupCreator = false"
        @get-groups="onGroupCreated"
        @push-to-groups="onGroupPushed"
      />
    </v-navigation-drawer>
    <v-overlay :value="showGroupCreator"></v-overlay>
  </div>
</template>

<script>
import Typography from "@/components/generic/Typography.vue";
import Button from "@/components/generic/Button.vue";
import InputChip from "@/components/generic/InputChip.vue";
import GroupTable from "@/components/data-table/Groups.vue";
import GroupTypeSelector from "@/components/groups/GroupTypeSelector.vue";
import { mapState } from "vuex";
import { debounce } from "@/shared_data/functions";

export default {
  name: "GroupsStep",
  components: {
    Typography,
    Button,
    InputChip,
    GroupTable,
    GroupTypeSelector,
  },
  props: {
    visibilityType: {
      type: String,
      default: "PRIVATE",
    },
    selectedGroups: {
      type: Array,
      default: () => [],
    },
    isEditing: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:visibilityType", "update:selectedGroups"],
  data() {
    return {
      search: null,
      debounceSearch: null,
      showGroupCreator: false,
    };
  },
  computed: {
    ...mapState(["permissions", "globalClientId"]),
    clientId() {
      return this.globalClientId;
    },
  },
  methods: {
    handleVisibilityChange(value) {
      this.$emit("update:visibilityType", value);
      if (value === "PUBLIC") {
        this.$emit("update:selectedGroups", [
          {
            groupId: 0,
            groupDisplayName: "Everyone",
          },
        ]);
        console.log("selectedGroups", this.selectedGroups);
      }
    },
    toggleGroup(group) {
      const groups = [...this.selectedGroups];
      const idx = groups.findIndex((g) => g.groupId === group.groupId);
      if (idx === -1) {
        groups.push(group);
      } else if (!group.dontRemove) {
        groups.splice(idx, 1);
      }
      this.$emit("update:selectedGroups", groups);
    },
    removeGroup(index) {
      const groups = [...this.selectedGroups];
      groups.splice(index, 1);
      this.$emit("update:selectedGroups", groups);
    },
    onGroupCreated(groupId) {
      this.search = undefined;
      this.debounceSearch = undefined;
      this.$nextTick(() => {
        if (this.$refs.groupTable) {
          this.$refs.groupTable.getGroups(true, "group-created", groupId);
        }
      });
    },
    onGroupPushed(group) {
      if (!group) return;
      const exists = this.selectedGroups.find((g) => g.groupId === group.groupId);
      if (!exists) {
        this.toggleGroup(group);
      }
    },
  },
  watch: {
    debounceSearch: debounce(function (newVal) {
      this.search = newVal;
    }, 500),
  },
};
</script>

<style scoped>
.groups-step {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
</style>
